import { FC, useEffect, useState, useCallback } from "react";
import { HorizontalStack, VerticalStack } from "../../Stack";
import { Checkbox, Text, Modal, PrimaryButton, Stack } from "@fluentui/react";
import { Document } from "../../../models";
import Draft from "../MultiShareModal/icons/Draft";
import Redline from "../MultiShareModal/icons/Redline";
import { useTranslation } from "react-i18next";
import { getFormattedDateTime } from "../../../utils";
import { useAppSelector, useAppDispatch } from "../../../hooks/hooks";
import "./selectDocument.css";
import PdfIcon from "../../../assets/Icons/svg/pdfIcon";
import CrossIcon from "../../../assets/Icons/svg/crossIcon";
import { colorPallete } from "../../../assets/color";
import { BlackDocumentIcon } from "../../../assets/Icons/svg/blackDocIcon";
import { t } from "i18next";
import Attachments from "../../../assets/Icons/svg/attachments";
import {getProjectDocuments} from "../../../modules/redux/Documents/document";
import { setDocumentWithVersions } from "../../../modules/redux/DocumentVersionSlice";
import { ApiClient } from "../../../network/ApiClient";
import { ApiEndpoints } from "../../../network/ApiEndpoints";
import SentIcon from "../MultiShareModal/icons/Sent";
import ReceivedIcon from "../../../assets/Icons/svg/receivedIcon";

type SelectDocumentProps = {
  document: Document;
  selectedDocuments: Document[];
  setSelectedDocuments: React.Dispatch<React.SetStateAction<Document[]>>;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  isSelectedAll: boolean;
  selectAllClicked?: boolean;
};

type SelectDocumentGroupProps = {
  selectedDocuments: Document[];
  setSelectedDocuments: React.Dispatch<React.SetStateAction<Document[]>>;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  documents?: Document[];
};

type SelectDocumentModalProps = {
  isModalOpen: boolean;
  hideModal: () => void;
  showModal: () => void;
  selectedDocuments: Document[];
  setSelectedDocuments: React.Dispatch<React.SetStateAction<Document[]>>;
  documents?: Document[];
  title?: string;
  actionButton?: {
    text: string;
    onClick: () => void;
    className?: string;
    disabled?: boolean;
  };
  icon?: React.ReactNode;
  warningTitle?: string;
  warningDescription?: string;
};

const selectDocumentStackStyle = (icon?: React.ReactNode) => ({
  gap: "10px",
  width: "auto",
  height: "42px",
  padding: "9px 20px",
  // margin: "3px 0",
  justifyContent: "flex-start",
  alignItems: "center",
  borderRadius: "6px",
  border: "1px solid #E8ECF4",
  cursor: "pointer",
  ":hover": {
    backgroundColor: "#F3F5F9",
    border: "1px solid #007CFF",
  },
  ...(icon && {
    width: "100%",
    background: "#fff",
    margin: "0",
  }),
});

const textStyle = {
  color: "#717070",
  fontFamily: "Poppins",
  fontSize: "9px",
  fontWeight: 400,
  lineHeight: "14px",
};

const dateStyle = {
  color: "#1F2430",
  fontFamily: "Poppins",
  fontSize: "9px",
  fontWeight: 600,
  lineHeight: "14px",
};

const selectDocumentGroupStyle = (icon?: React.ReactNode) => ({
  maxHeight: "550px",
  background: "#FFF",
  margin: "14px",
  display: "flex",
  gap: "6px",
  overflowY: "auto",
  ...(icon && {
    maxHeight: "400px",
    background: "linear-gradient(90deg, #ECF0F9 0%, #EFECF9 100%)",
    borderRadius: "12px",
    margin: "0",
    width: "100%",
    padding: "14px 10px",
  }),
});

const selectDocumentContainer = (icon?: React.ReactNode) => ({
    width: "100%",
    ...(icon && {
      padding: "15px",
    })
  });

const documentNameStyle = {
  color: "#1F2430",
  fontFamily: "Poppins",
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: "14px",
  width: "238px",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const SelectDocument: FC<SelectDocumentProps & {icon?: React.ReactNode}> = (props) => {
  const { t } = useTranslation();
  const { setSelectedDocuments, setError, document, selectedDocuments } = props;

  const [selectedVersion, setSelectedVersion] = useState(() => {
    const initialSelectedVersion = selectedDocuments.find(
      (doc) => doc.rootDocumentId === document.rootDocumentId
    );

    return initialSelectedVersion || document.versions[0];
  });
  const [checked, setChecked] = useState(
    selectedDocuments.some(
      (doc) => doc.rootDocumentId === document.rootDocumentId
    )
  );
  // DEX-573: the parent seeds `document.versions` with just the latest version (from /root)
  // so the modal renders instantly. Load the full version list lazily the first time this
  // doc's dropdown is opened — avoids a /versions call per document on modal open.
  const [versions, setVersions] = useState<Document[]>(document.versions || []);
  const [fullVersionsLoaded, setFullVersionsLoaded] = useState(
    (document.versions?.length ?? 0) > 1
  );

  useEffect(() => {
    setVersions(document.versions || []);
    setFullVersionsLoaded((document.versions?.length ?? 0) > 1);
  }, [document.versions]);

  const loadFullVersions = useCallback(async () => {
    if (fullVersionsLoaded) return;
    try {
      // Fetch via ApiClient, NOT the getDocumentversions thunk: the thunk writes the shared
      // fetchDocumentVersion slice that drives the document page behind the modal, which made
      // the cards flash and the version section blank. This lookup stays local to the modal.
      const res: any = await ApiClient.get(
        ApiEndpoints.getDocumentVersions(document.rootDocumentId),
        {},
        {}
      );
      const full = res?.data?.data || res?.data || [];
      if (Array.isArray(full) && full.length) {
        setVersions(full);
        setFullVersionsLoaded(true);
      }
    } catch (e) {
      // keep the seeded latest version on failure
    }
  }, [document.rootDocumentId, fullVersionsLoaded]);

  const handleSelectChange = (event: any) => {
    const selectedIndex = event.target.selectedIndex;
    const newSelectedVersion = versions[selectedIndex];
    setSelectedVersion(newSelectedVersion);
  };

  const handleCheckboxChange = (e: any) => {
    setChecked((chec) => !chec);
  };

  const handleDocumentStackClick = () => {
    setChecked((prev) => !prev);
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleDocumentStackClick();
  };

  useEffect(() => {
    if (props.selectAllClicked) {
      if (props.isSelectedAll) {
        setChecked(true);
      } else {
        setChecked(false);
      }
    }
  }, [props.isSelectedAll, props.selectAllClicked]);

  useEffect(() => {
    if (checked) {
      setSelectedDocuments((prevSelectedDocuments) => {
        const filteredDocuments = prevSelectedDocuments.filter(
          (doc) => doc.rootDocumentId !== document.rootDocumentId
        );
        return [...filteredDocuments, selectedVersion];
      });
      setError(false);
    } else {
      setSelectedDocuments((prevSelectedDocuments) =>
        prevSelectedDocuments.filter(
          (doc) => doc.rootDocumentId !== document.rootDocumentId
        )
      );
    }
  }, [checked, selectedVersion, setSelectedDocuments, setError, document.rootDocumentId]);

  function getIcon(isRedlined: any, fileFormat: string) {
    if (isRedlined) {
      return <Redline />;
    } else {
      return fileFormat === "pdf" ? <PdfIcon /> : <Draft />;
    }
  }

  function getSentReceivedIcon(documentVersion: any) {
    if (documentVersion.fileType === "sent") {
      return <SentIcon />;
    } else if (documentVersion.fileType === "received") {
      return <ReceivedIcon />;
    } else {
      return null;
    }
  }

  return (
    <HorizontalStack
      data-testid="document-stack"
      styles={{ root: selectDocumentStackStyle(!!props.icon) }}
      onClick={handleDocumentStackClick}
    >
      <HorizontalStack
        style={{
          alignItems: "center",
          gap: "12px",
          width: "50%",
        }}
        >
          <div onClick={handleCheckboxClick}>
            <Checkbox
              styles={{
                checkbox: {
                  width: "12px",
                  height: "12px",
                  stroke: "#007CFF",
                  strokeWidth: "0.8px",
                  borderColor: "#007CFF",
                },
              }}
              checked={checked}
              onChange={handleCheckboxChange}
            />
          </div>
        <HorizontalStack style={{ gap: "4px", minWidth: "60px" }}>
          {getIcon(
            selectedVersion.metadata.isRedlined,
            selectedVersion.metadata.fileType
          )}
          {getSentReceivedIcon(selectedVersion)}
          {selectedVersion?.attachment && (<>
            <Attachments />
            <PdfIcon />
          </>)}
        </HorizontalStack>
        <Text style={{ whiteSpace: "nowrap", ...documentNameStyle }}>
          {document.rootDocumentName?.replace(/\.[^/.]+$/, "")}
        </Text>
      </HorizontalStack>
      <HorizontalStack
        style={{
          alignItems: "center",
          justifyContent: "space-between",
          width: "50%",
        }}
      >
        {versions.length > 0 &&
        <select
          style={{ ...selectStyle }}
          id="versionSelect"
          onChange={handleSelectChange}
          value={selectedVersion ? `v${selectedVersion.version}` : ""}
          onMouseDown={loadFullVersions}
          onFocus={loadFullVersions}
          onClick={event => event.stopPropagation()}
        >
          {versions.map(
            (version, index) =>
              version.version !== null && (
                <option key={index} value={`v${version.version}`}>
                  {index === 0
                    ? `v${version?.version} ${t("most_recent")}`
                    : `v${version?.version}`}
                </option>
              )
          )}
        </select>
        }
        <HorizontalStack
          styles={{
            root: { gap: "4px", minWidth: "155px", justifyContent: "flex-end" },
          }}
        >
          <Text style={textStyle}>
            {selectedVersion?.fileType === "draft"
              ? t("last_saved_on")
              : selectedVersion?.fileType === "sent"
              ? t("sent_on")
              : t("received_on")}
          </Text>
          <Text style={dateStyle}>
            {
              getFormattedDateTime(
                new Date(selectedVersion?.lastUpdatedDate!)
              )[0]
            }
            &nbsp;
            {
              getFormattedDateTime(
                new Date(selectedVersion?.lastUpdatedDate!)
              )[1]
            }
          </Text>
        </HorizontalStack>
      </HorizontalStack>
    </HorizontalStack>
  );
};

const SelectDocumentGroup: FC<SelectDocumentGroupProps & {icon?: React.ReactNode}> = (props) => {
  const dispatch = useAppDispatch();
  const projectState = useAppSelector(
    (state: { fetchProjectDetails: any }) => state.fetchProjectDetails
  );
  const projectDocumentsFromRedux = useAppSelector(
    (state: { fetchProjectDocuments: any }) => state.fetchProjectDocuments
  );
  
  let documents = useAppSelector(
    (state) => state.fetchDocumentWithVersions.documents
  );

  // DEX-573: seed each document with just its latest version (from /root, M3) so the modal
  // renders immediately instead of blocking on a per-document /versions fetch. The full
  // version list is loaded lazily by each row when its version dropdown is opened.
  const seedDocumentsWithLatest = useCallback((docs: Document[]) => {
    const seeded: Document[] = docs.map((doc: any) => ({
      ...doc,
      versions: doc.latestVersion
        ? [doc.latestVersion]
        : doc.versions?.length
        ? doc.versions
        : [],
    }));
    dispatch(setDocumentWithVersions(seeded));
  }, [dispatch]);

  useEffect(() => {
    if (!projectState.data?.id) return;
    // DEX-573: the document page already loaded this project's docs into the shared
    // fetchProjectDocuments slice. Refetching here flips its isLoading, which makes the page
    // behind swap its cards for a loader (a flash). Only fetch if the slice doesn't already
    // hold this project's documents (row projectId comes from latestVersion, M3).
    const data = projectDocumentsFromRedux?.data;
    const sliceProjectId =
      Array.isArray(data) && data.length > 0
        ? data[0]?.projectId ?? data[0]?.latestVersion?.projectId
        : undefined;
    if (sliceProjectId === projectState.data.id) return;
    dispatch(getProjectDocuments(projectState.data.id));
  }, [projectState.data?.id, dispatch, projectDocumentsFromRedux?.data]);

  useEffect(() => {
    const data = projectDocumentsFromRedux?.data;
    if (data && data.length > 0) {
      // Re-seed only when the document set changes (count or first doc) — not when a row
      // swaps in its own fully-loaded versions, which must not trigger a reset loop.
      const needsSeed =
        documents.length !== data.length ||
        documents[0]?.rootDocumentId !== data[0]?.rootDocumentId;
      if (needsSeed) {
        seedDocumentsWithLatest(data);
      }
    }
  }, [projectDocumentsFromRedux?.data, seedDocumentsWithLatest, documents]);

  if (documents.length === 0 && props.documents && props.documents.length > 0) {
    documents = props.documents;
  }
  const [isSelectedAll, setIsSelectedAll] = useState(false);
  const [selectAllClicked, setSelectAllClicked] = useState(false);
  
  function handleSelectedAllCheckboxChange() {
    setSelectAllClicked(true);
    setIsSelectedAll((prev) => !prev);
    if (!isSelectedAll) {
      props.setSelectedDocuments(documents.map((doc: Document) => doc.versions ? doc.versions[0] : doc));
      props.setError(false);
    } else {
      props.setSelectedDocuments([]);
    }
  }
  
  useEffect(() => {
    if (props.selectedDocuments.length === documents.length && documents.length > 0) {
      setIsSelectedAll(true);
    } else {
      setIsSelectedAll(false);
    }
    setSelectAllClicked(false);
  }, [props.selectedDocuments, documents.length]);
  
  return (
    <VerticalStack styles={{ root: selectDocumentGroupStyle(!!props.icon) }}>
      <HorizontalStack>
        <Checkbox
          styles={{
            checkbox: {
              width: "12px",
              height: "12px",
              stroke: "#007CFF",
              strokeWidth: "0.8px",
              borderColor: "#007CFF",
            },
            root: {
              // width: "12px",
              margin: "2px 20px",
              height: "12px",
            },
            label: {
              fontSize: "10px",
              height: "12px",
              lineHeight: "12px",
            },
            input: {
              height: "12px",
              width: "12px",
            },
            text: {
              fontSize: "10px",
              height: "12px",
              lineHeight: "12px",
            },
          }}
          label="Select All"
          checked={isSelectedAll}
          onChange={handleSelectedAllCheckboxChange}
        />
      </HorizontalStack>
      {documents.map((document: Document) => (
        <SelectDocument
          key={document.rootDocumentId}
          document={document}
          selectedDocuments={props?.selectedDocuments}
          setSelectedDocuments={props?.setSelectedDocuments}
          setError={props?.setError}
          isSelectedAll={isSelectedAll}
          selectAllClicked={selectAllClicked}
          icon={props.icon}
        />
      ))}
    </VerticalStack>
  );
};

const SelectedDocumentsCount: FC<{ num: number }> = ({ num }) => {
  return (
    <HorizontalStack style={styles.selectedDocContainer}>
      <BlackDocumentIcon />
      <Text style={styles.selectedTextStyle}>{`${num}`} </Text>
      <Text style={styles.selectedTextStyle}>
        {" "}
        {num > 1 ? t("documents_selected") : t("document_selected")}{" "}
      </Text>
    </HorizontalStack>
  );
};

const SelectDocumentModal: FC<SelectDocumentModalProps> = (props) => {
  const [error, setError] = useState(false);

  const Title = () => {
    return (
      <HorizontalStack style={styles.titleContainer}>
        <Text
          styles={{
            root: {
              color: "#323232",
              fontFamily: "Poppins",
              fontSize: "18px",
              fontWeight: 500,
              lineHeight: "26px",
            },
          }}
        >
          {props.title || t("select_documents")}
        </Text>
        <Stack
          onClick={() => {
            props.hideModal();
          }}
          style={{ cursor: "pointer" }}
        >
          <CrossIcon />
        </Stack>
      </HorizontalStack>
    );
  };

  const modalStyles = {
    root: {
      background: "rgba(0, 0, 0, 0.5)",
    },
    main: {
      borderRadius: "12px",
      width: "752px",
      padding: "18px 8px 8px 8px",
      alignItems: "center",
      background: "linear-gradient(90deg, #ECF0F9 0%, #EFECF9 100%)",
    },
  };

  return (
    <>
      <Modal
        styles={modalStyles}
        isOpen={props.isModalOpen}
        onDismiss={props.hideModal}
        isBlocking={true}
      >
        <Title />
        <VerticalStack
          styles={{
            root: {
              border: "1px solid #D8E1EB",
              background: "#FFF",
              boxShadow: "0px 4px 10px 0px rgba(0, 0, 0, 0.08)",
              borderRadius: "8px",
              alignItems: "center",
              // padding: "14px 20px"
            },
          }}
        >
        {props.icon && (
          <div style={{width: "100%", padding: "14px 20px"}}>
            <div>{props.icon}</div>
            <div style={{fontWeight: "600", margin: "5px 0", color: "#323232"}}>{props.warningTitle}</div>
            <div style={{color: "#717070"}}>{props.warningDescription}</div>
          </div>
        )}
          <div style={selectDocumentContainer(!!props.icon)}>
            <SelectDocumentGroup
              selectedDocuments={props?.selectedDocuments || []}
              setSelectedDocuments={props?.setSelectedDocuments}
              setError={setError}
              documents={props?.documents || []}
              icon={props.icon}
            />
          </div>
          {error && (
            <Text style={styles.errorText}>
              {t("please_select_documents")}
            </Text>
          )}
          <HorizontalStack style={styles.documentContainer}>
            <SelectedDocumentsCount num={props?.selectedDocuments?.length || 0} />
            <HorizontalStack style={{ gap: "8px", alignItems: "center" }}>
              {props.actionButton && (
                <PrimaryButton
                  text={props.actionButton.text}
                  disabled={props.actionButton.disabled}
                  styles={{
                    root: {
                      padding: "12px 24px",
                      backgroundColor: props.actionButton.disabled ? "#717070" : "#18468F",
                      borderRadius: "4px",
                      minWidth: "120px",
                      height: "40px",
                    },
                    label: {
                      color: "#F4F4F4",
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      fontWeight: 500,
                      lineHeight: "16px",
                      margin: 0,
                    },
                    rootHovered: {
                      backgroundColor: props.actionButton.disabled ? "#717070" : "#18468F",
                    },
                  }}
                  onClick={() => {
                    if (!props?.selectedDocuments || props?.selectedDocuments?.length === 0) {
                      setError(true);
                      return;
                    }
                    props.actionButton?.onClick();
                  }}
                  className={props.actionButton.className}
                />
              )}
              <PrimaryButton
                text={String(t("cancel"))}
                styles={{
                  root: {
                    padding: "12px 24px",
                    backgroundColor: "#E8ECF4",
                    borderRadius: "4px",
                    minWidth: "120px",
                    height: "40px",
                    border: "1px solid #E8ECF4",
                    transition: "all .2s ease-in-out",
                  },
                  label: {
                    color: "#323232",
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "16px",
                    margin: 0,
                  },
                  rootHovered: {
                    backgroundColor: "white",
                    border: "1px solid #323232",
                    transition: "all .2s ease-in-out",
                  },
                }}
                onClick={() => {
                  props?.setSelectedDocuments([]);
                  props.hideModal();
                }}
              />
            </HorizontalStack>
          </HorizontalStack>
        </VerticalStack>
      </Modal>
    </>
  );
};

const styles = {
  loadingContainer: {
    minHeight: "112px",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
  },
  loadingText: {
    fontFamily: "Poppins",
    fontSize: "16px",
  },
  documentContainer: {
    justifyContent: "space-between",
    width: "100%",
    padding: "14px 20px",
    alignItems: "center",
  },
  errorText: {
    display: "flex",
    alignSelf: "flex-start",
    paddingLeft: "20px",
    color: colorPallete.btRed,
  },
  titleContainer: {
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0 14px 10px 16px",
  },
  selectedTextStyle: {
    color: "#323232",
    fontFamily: "Poppins",
    fontSize: "12px",
    lineHeight: "26px",
  },
  selectedDocContainer: {
    justifyContent: "flex-start",
    gap: "4px",
    alignItems: "center",
    marginLeft: "2px",
  },
};

const selectStyle = {
  width: "132px",
  borderRadius: "4px",
  border: "1px solid var(--LIGHT-BLUE, #E8ECF4)",
  background: "#F8F8F8",
  padding: "4px",
  color: "#18468F",
  fontFamily: "Poppins",
  fontSize: "12px",
  fontWeight: 500,
  cursor: "pointer",
};

export { SelectDocument, SelectDocumentGroup, SelectDocumentModal };
